/**
 * The calendar's arithmetic (`specs/M4-V4-calendar.md` §5, §7), all pure.
 *
 * Days are strings throughout — monday stores `YYYY-MM-DD` and a DST boundary must not move
 * an item a day (`[V4§10 E6]`), so nothing here builds a `Date` from a local offset.
 */
import { addDays, dayFromIndex, dayIndex, type CalendarRange } from '@elizmonday/shared';

export interface DayCell {
  readonly day: string;
  /** `false` for the leading / trailing days of the neighbouring months. */
  readonly inRange: boolean;
  readonly isToday: boolean;
}

export const WEEKDAY_LABELS_MONDAY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export const WEEKDAY_LABELS_SUNDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export function weekdayLabels(weekStartsOn: 0 | 1): readonly string[] {
  return weekStartsOn === 1 ? WEEKDAY_LABELS_MONDAY : WEEKDAY_LABELS_SUNDAY;
}

/** 0 = Sunday … 6 = Saturday, from the date string alone. */
export function weekdayOf(day: string): number {
  return ((dayIndex(day) % 7) + 11) % 7;
}

/** The first day of the week `day` falls in. */
export function startOfWeek(day: string, weekStartsOn: 0 | 1): string {
  const offset = (weekdayOf(day) - weekStartsOn + 7) % 7;
  return addDays(day, -offset);
}

export function startOfMonth(day: string): string {
  return `${day.slice(0, 7)}-01`;
}

export function addMonths(day: string, delta: number): string {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7)) - 1 + delta;
  const target = new Date(Date.UTC(year, month, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  const dayOfMonth = Math.min(Number(day.slice(8, 10)), lastDay);
  return `${target.toISOString().slice(0, 7)}-${String(dayOfMonth).padStart(2, '0')}`;
}

/** The grid a range renders: whole weeks for `month`, one week for `week`, one day for `day`. */
export function calendarGrid(input: {
  anchor: string;
  range: CalendarRange;
  weekStartsOn: 0 | 1;
  today: string;
}): { days: DayCell[]; start: string; end: string; columns: number } {
  const { anchor, range, weekStartsOn, today } = input;
  if (range === 'day') {
    return {
      days: [{ day: anchor, inRange: true, isToday: anchor === today }],
      start: anchor,
      end: anchor,
      columns: 1,
    };
  }
  if (range === 'week') {
    const start = startOfWeek(anchor, weekStartsOn);
    const days = Array.from({ length: 7 }, (_, index) => {
      const day = addDays(start, index);
      return { day, inRange: true, isToday: day === today };
    });
    return { days, start, end: addDays(start, 6), columns: 7 };
  }
  const monthStart = startOfMonth(anchor);
  const monthPrefix = monthStart.slice(0, 7);
  const gridStart = startOfWeek(monthStart, weekStartsOn);
  const monthEnd = addDays(addMonths(monthStart, 1), -1);
  // Whole weeks until the month is covered — 5 rows for most months, 6 when it spills.
  const weeks = Math.ceil((dayIndex(monthEnd) - dayIndex(gridStart) + 1) / 7);
  const days: DayCell[] = [];
  for (let index = 0; index < weeks * 7; index += 1) {
    const day = dayFromIndex(dayIndex(gridStart) + index);
    days.push({ day, inRange: day.slice(0, 7) === monthPrefix, isToday: day === today });
  }
  return { days, start: gridStart, end: days[days.length - 1]?.day ?? gridStart, columns: 7 };
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/** monday's own header text: `September 2026` in month, the week's span otherwise. */
export function rangeTitle(grid: { start: string; end: string }, range: CalendarRange, anchor: string): string {
  if (range === 'month') {
    return `${MONTHS[Number(anchor.slice(5, 7)) - 1] ?? ''} ${anchor.slice(0, 4)}`;
  }
  if (range === 'day') {
    return `${MONTHS[Number(anchor.slice(5, 7)) - 1] ?? ''} ${String(Number(anchor.slice(8, 10)))}, ${anchor.slice(0, 4)}`;
  }
  const from = `${MONTHS[Number(grid.start.slice(5, 7)) - 1] ?? ''} ${String(Number(grid.start.slice(8, 10)))}`;
  const to = `${MONTHS[Number(grid.end.slice(5, 7)) - 1] ?? ''} ${String(Number(grid.end.slice(8, 10)))}`;
  return `${from} – ${to}, ${grid.end.slice(0, 4)}`;
}

/** `‹` / `›` move by the visible range, never by a fixed month. */
export function shiftAnchor(anchor: string, range: CalendarRange, delta: number): string {
  if (range === 'month') return addMonths(anchor, delta);
  return addDays(anchor, delta * (range === 'week' ? 7 : 1));
}

/** The viewer's own day, as a `YYYY-MM-DD` string in their local calendar. */
export function todayString(now: Date = new Date()): string {
  return `${String(now.getFullYear()).padStart(4, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}
